'use client';

import { cn } from '@heidi/utils';
import { Avatar, AvatarFallback, AvatarImage, getAvatarColor } from '../../../avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../dropdown-menu';
import { TypographyCaptionBold, TypographyFootNote } from '../../../typography';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useCollapsibleVariant,
  useSidebar,
} from '../sidebar';
import {
  userAccountMenuAvatarClasses,
  userAccountMenuButtonVariants,
  userAccountMenuContentClasses,
  userAccountMenuEmailVariants,
  userAccountMenuNameVariants,
  userAccountMenuUserInfoVariants,
} from './user-account-menu.styles';
import type { UserAccountMenuProps } from './user-account-menu.types';

export function UserAccountMenu({
  user,
  colorSeed,
  children,
  avatarOnlyTrigger = false,
  sideOffset = 4,
  className,
  contentClassName,
  appearanceMenuSlot,
  'data-testid': testId,
}: UserAccountMenuProps) {
  const { isMobile } = useSidebar();
  const collapsible = useCollapsibleVariant();
  const avatarColor = getAvatarColor(colorSeed || user.name);
  // `sm` = 24px, `md` = 32px; the size prop also drives the fallback text.
  const triggerAvatarSize = isMobile || collapsible === 'label-icons' ? 'md' : 'sm';
  const labelAvatarSize = isMobile ? 'md' : 'sm';

  return (
    <SidebarMenu data-testid={testId} className={className}>
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            {avatarOnlyTrigger ? (
              <Avatar
                role="button"
                tabIndex={0}
                aria-label={user.name}
                color={avatarColor}
                size={triggerAvatarSize}
                data-testid="user-dropdown-trigger"
                className="mx-auto cursor-pointer transition-opacity duration-200 ease-out hover:opacity-90 motion-reduce:transition-none"
              >
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            ) : (
              <SidebarMenuButton
                size="lg"
                data-testid="user-dropdown-trigger"
                className={cn(
                  userAccountMenuButtonVariants({ collapsible, mobile: isMobile })
                )}
              >
                <Avatar
                  color={avatarColor}
                  size={triggerAvatarSize}
                  className={userAccountMenuAvatarClasses}
                >
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div
                  className={cn(
                    userAccountMenuUserInfoVariants({ collapsible, mobile: isMobile })
                  )}
                >
                  <TypographyCaptionBold
                    className={cn(userAccountMenuNameVariants({ mobile: isMobile }))}
                  >
                    {user.name}
                  </TypographyCaptionBold>
                  <TypographyFootNote
                    className={cn(userAccountMenuEmailVariants({ mobile: isMobile }))}
                  >
                    {user.email}
                  </TypographyFootNote>
                </div>
              </SidebarMenuButton>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(userAccountMenuContentClasses, contentClassName)}
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={sideOffset}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2.5 py-1.5 text-start">
                <Avatar
                  color={avatarColor}
                  size={labelAvatarSize}
                  className={userAccountMenuAvatarClasses}
                >
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div
                  className={cn(userAccountMenuUserInfoVariants({ mobile: isMobile }))}
                >
                  <TypographyCaptionBold className="truncate text-text-primary">
                    {user.name}
                  </TypographyCaptionBold>
                  <TypographyFootNote className="truncate text-text-secondary">
                    {user.email}
                  </TypographyFootNote>
                </div>
              </div>
            </DropdownMenuLabel>
            {appearanceMenuSlot}
            {children && (
              <>
                <DropdownMenuSeparator />
                {children}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
